import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from '../../../../hooks/useTranslation';
import colors from '../../../../themes/main-colors';
import { useIsMobile } from '../../../../util/style-utils';
import {
  Box,
  Button,
  Divider,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { ToggleSwitch } from '../common/toggle-switch';
import ReactMarkdown from 'react-markdown/with-html';
import ROUTES from '../../../../routes';
import useAccountForm from '../../../../hooks/useAccountForm';
import EmailField from '../../../email-text-field';
import PasswordField from '../../../password-text-field';
import isEmpty from 'lodash/isEmpty';
import PGEButton from '../../../pge-button/_PgeButton';
import { useOutageLeftPanelState } from '../../providers/OutageLeftPanelStateProvider';
import { InputChangeEvent } from '../../../../hooks/useFormState.types';
import { SendOtlResponse } from '../../../../__generated__/pge-types';
import { sendOTLQuery } from '../../queries/queries';
import { useApolloClient } from '@apollo/react-hooks';
import { getFromStorage } from '../../../../util/storage-utils';
import { NotificationsContext } from '../../../../providers/NotificationsProvider';
import useWrapWithLoader from '../../../../hooks/useWrapWithLoading';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: 44,
    height: 24,
    padding: 0,
    margin: theme.spacing(1),
  },
  thumb: {
    width: 20,
    height: 20,
  },
  font16: {
    fontSize: 16,
    fontWeight: 600,
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    padding: '1rem 2rem',
    marginLeft: '0.5rem',
    [theme.breakpoints.down('sm')]: {
      padding: '1rem 2rem',
    },
  },
  content: {
    background: colors.white,
    padding: '1.5rem',
    margin: '0 1.5rem 1.5rem 1.5rem',
  },
  inputValues: {
    display: 'grid',
    gap: '30px',
  },
  rememberMe: {
    alignItems: 'center',
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '1.5rem',
    },
  },
  signInGrid: {
    marginTop: '1rem',
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  divider: {
    marginTop: '1rem',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '1rem 2rem 0 2rem',
    [theme.breakpoints.down('sm')]: {
      margin: 0,
    },
  },
  submit: {
    backgroundColor: colors.orange,
    borderColor: colors.orange,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: colors.orange,
      borderColor: colors.orange,
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  secureContent: {
    background: colors.white,
    padding: '1.5rem',
    margin: '0 1.5rem 1.5rem 1.5rem',
  },
  secureInfo: {
    background: '#fff9c4',
    padding: '1rem',
    marginTop: '1rem',
    borderRadius: 4,
  },
  secureInfoList: {
    margin: 0,
    paddingLeft: '1.2rem',
  },
  secureButtonLarge: {
    borderRadius: '25px',
    background: colors.orange,
    color: colors.white,
    width: '100%',
    padding: '0.5rem',
    fontSize: 18,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: colors.orange,
      borderColor: colors.orange,
    },
  },
  continueLink: {
    color: colors.sparkBlue,
    marginTop: '0.75rem',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  secureLinkText: {
    fontSize: 16,
  },
  listStyles: {
    marginBottom: 5,
  },
}));

export const OneTimeLoginTokenErrors = Object.freeze({
  AccountLocked: 'AccountLocked',
});

export const ReportOutageSignIn = () => {
  const { t, richT } = useTranslation();
  const isMobile = useIsMobile();
  const classes = useStyles();
  const [toggleSwitch, setToggleSwitch] = useState<boolean>(false);
  const {
    setReportOutageOpen,
    setAccountNotFoundInfo,
  } = useOutageLeftPanelState();
  const apolloClient = useApolloClient();
  const { form, handleSubmit } = useAccountForm({
    redirectUrl: ROUTES.OUTAGES,
  });
  const notificationContext = useContext(NotificationsContext);
  const { wrapWithLoader } = useWrapWithLoader();

  const onFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      await handleSubmit(e);
    } finally {
      setReportOutageOpen(false);
      setAccountNotFoundInfo(undefined);
    }
  };

  const HandleError = (errorMessage: any) => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    notificationContext.setState({
      isOpen: true,
      message: errorMessage,
      severity: 'error',
    });
    typeof window !== 'undefined' && window.scrollTo(0, 0);
  };

  const emailFromStorage: string | undefined = useMemo(
    () => getFromStorage('userEmail'),
    [],
  );
  const defaultEmail = emailFromStorage;

  const onQuickLoginSubmit = wrapWithLoader(async () => {
    const { data: oneTimeLoginData, errors } = await apolloClient.query<{
      sendOTL: SendOtlResponse;
    }>({
      query: sendOTLQuery,
      variables: {
        params: {
          email: form.values.email
            ? form.values.email.toLowerCase()
            : defaultEmail?.toLowerCase(),
        },
      },
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    });
    if (errors?.length) {
      if (errors[0].message?.trim() === OneTimeLoginTokenErrors.AccountLocked) {
        HandleError(
          richT('PASSWORD_ATTEMPTS_EXCEEDED_DAILY_LIMIT', {
            CUSTOMER_SERVICE_NUMBER: `tel:${t('CALL_FOR_ASSISTANCE_NUMBER')}`,
          }),
        );
      } else {
        HandleError(t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY'));
      }
    } else if (oneTimeLoginData?.sendOTL?.isSuccess) {
      notificationContext.setState({
        isOpen: true,
        message: t('ONE_TIME_LOGIN_SUBMIT_MESSAGE'),
        severity: 'success',
      });
    }
  });

  return (
    <div>
      <Box>
        <Grid className={classes.header}>
          <ToggleSwitch
            classes={{
              root: classes.root,
              thumb: classes.thumb,
            }}
            checked={toggleSwitch}
            size={'medium'}
            onChange={e => {
              setToggleSwitch(e.target.checked);
            }}
          />
          <Typography className={classes.font16}>
            {t('USE_QUICK_LOGIN')}
          </Typography>
        </Grid>
        {toggleSwitch ? (
          <Box className={classes.secureContent}>
            <Typography className={classes.secureLinkText}>
              {t('REQUEST_SECURE_LINK')}
            </Typography>
            <Box mt={1}>
              <EmailField
                autoFocus
                autoComplete="username"
                inputProps={{ maxLength: 50 }}
                {...form.props('email')}
                id="login-email"
                label={t('REGISTERED_EMAIL_ADDRESS')}
              />
            </Box>
            <Box className={classes.secureInfo}>
              <ul className={classes.secureInfoList}>
                <li className={classes.listStyles}>
                  {t('SECURE_FEATURE_NOTE_1')}
                </li>
                <li className={classes.listStyles}>
                  {t('SECURE_FEATURE_NOTE_2')}
                </li>
                <li>{t('SECURE_FEATURE_NOTE_3')}</li>
              </ul>
            </Box>
            <Box
              mt={2}
              display={'flex'}
              flexDirection={'column'}
              alignItems={'center'}
            >
              <Button
                className={classes.secureButtonLarge}
                onClick={() => onQuickLoginSubmit()}
              >
                {t('SEND_SECURE_LINK')}
              </Button>
              <Typography
                className={classes.continueLink}
                onClick={() => {
                  setReportOutageOpen(false);
                }}
              >
                {t('CONTINUE_AS_GUEST')}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box className={classes.content}>
            <Box className={classes.inputValues}>
              <>
                <form id={'login-form'} noValidate onSubmit={onFormSubmit}>
                  <Grid item>
                    <EmailField
                      autoFocus
                      autoComplete="username"
                      inputProps={{ maxLength: 50 }}
                      {...form.props('email')}
                      id="login-email"
                    />
                  </Grid>
                  <Grid item>
                    <PasswordField
                      defaultShow={isMobile}
                      autoComplete="current-password"
                      {...form.props('password')}
                      id="login-password"
                    />
                  </Grid>
                </form>
              </>
            </Box>
            <Grid className={classes.signInGrid}>
              <Grid item className={classes.rememberMe}>
                <ToggleSwitch
                  classes={{
                    root: classes.root,
                    thumb: classes.thumb,
                  }}
                  checked={form.values.rememberMe}
                  size={'medium'}
                  name={'rememberMe'}
                  onChange={(e, value) => {
                    const target = (e && (e.target as any)) || null;
                    if (
                      target &&
                      typeof (target as HTMLElement).setAttribute === 'function'
                    ) {
                      if (value) {
                        (target as HTMLElement).setAttribute('checked', '');
                      } else {
                        (target as HTMLElement).removeAttribute('checked');
                      }
                    }
                    if ((e as any).persist) {
                      (e as any).persist();
                    }
                    void form.onChange(e as InputChangeEvent);
                  }}
                />
                <Typography className={classes.font16}>
                  {t('REMEMBER_ME')}
                </Typography>
              </Grid>
              <Grid item>
                <PGEButton
                  id="sign-in-submit-btn"
                  type={'submit'}
                  form={'login-form'}
                  disableRippleEffect
                  theme={'Primary'}
                  className={classes.submit}
                  disabled={!isEmpty(form.errors)}
                >
                  {t('SIGN_IN')}
                </PGEButton>
              </Grid>
            </Grid>
            <Divider className={classes.divider} />
            <Grid className={classes.footer}>
              <Typography>
                <ReactMarkdown
                  source={t('FORGOT_PASSWORD_NEW', {
                    FORGOT_PASSWORD: ROUTES.FORGOT_PASSWORD,
                  })}
                  escapeHtml={false}
                />
              </Typography>
              <Typography>
                <ReactMarkdown
                  source={t('REGISTER_ACCOUNT', {
                    REGISTER_ACCOUNT: ROUTES.REGISTRATION,
                  })}
                  escapeHtml={false}
                />
              </Typography>
            </Grid>
          </Box>
        )}
      </Box>
    </div>
  );
};
